import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Bed, Plus, ShoppingBag, Trash2 } from 'lucide-react'

import { useHotelAvailability } from '@/components/HotelAvailabilityContext'
import { usePeriodsForReservation } from '@/shared/hooks/use-periods-for-reservation'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { BufferedNumberInput } from '@/shared/ui/buffered-number-input'
import { Input } from '@/shared/ui/input'
import { ServiceItem } from '@/shared/ui/service-item'
import { formatCurrency } from '@/shared/utils/format'
import { calculateTotalPrice, hasHighSeasonDays } from '@/shared/utils/pricing'
import { ClientClassification } from '@monorepo/functions/src/types/client'
import { Extra, HotelReservation } from '@monorepo/functions/src/types/reservations'
import { AdditionalService, AdditionalServiceType } from '@monorepo/functions/src/types/services'

interface PriceBreakdownCardProps {
    reservation: HotelReservation
    onExtrasChange: (extras: Extra[]) => void
    onServicesChange: (services: AdditionalService[]) => void
    onShopProductsChange: (shopProducts: HotelReservation['shopProducts']) => void
    onGoToShop: () => void
    clientClassification?: ClientClassification
}

export function PriceBreakdownCard({
    reservation,
    onExtrasChange,
    onServicesChange,
    onShopProductsChange,
    onGoToShop,
    clientClassification,
}: PriceBreakdownCardProps) {
    const { t } = useTranslation()
    const [newExtra, setNewExtra] = useState<{ concept: string; amount: number }>({ concept: '', amount: 0 })
    const { periods, loading: periodsLoading } = usePeriodsForReservation(
        reservation.checkInDate,
        reservation.checkOutDate,
    )
    const { highSeasonPeriods } = useHotelAvailability()

    const priceData = useMemo(() => {
        if (periodsLoading || !periods.length) {
            return { nights: 0, petsPriceByDay: 0, total: 0 }
        }
        return calculateTotalPrice(reservation, periods, clientClassification, highSeasonPeriods)
    }, [reservation, periods, periodsLoading, clientClassification, highSeasonPeriods])

    const { nights, petsPriceByDay, total } = priceData

    // State to track number of days and price per day for each service
    // Key format: `${petIndex}-${serviceType}`
    const [serviceInputs, setServiceInputs] = useState<Record<string, { days: number | ''; pricePerDay: number | '' }>>(
        {},
    )

    // Create a stable key based on service identifiers to avoid unnecessary re-runs
    // This only changes when services are added/removed, not when they're updated
    const servicesKey = useMemo(
        () =>
            reservation.additionalServices
                .filter(s => s.type !== 'hairdressing' && s.type !== 'driver' && 'petIndex' in s)
                .map(s => `${s.petIndex}-${s.type}`)
                .sort()
                .join(','),
        [reservation.additionalServices],
    )

    // Initialize service inputs from stored prices when reservation changes
    useEffect(() => {
        setServiceInputs(prev => {
            const updated: Record<string, { days: number | ''; pricePerDay: number | '' }> = {
                ...(prev as Record<string, { days: number | ''; pricePerDay: number | '' }>),
            }
            let hasChanges = false

            reservation.additionalServices.forEach(service => {
                if (service.type !== 'hairdressing' && service.type !== 'driver' && 'petIndex' in service) {
                    const key = `${service.petIndex}-${service.type}`
                    // Only initialize if not already set (preserve user input)
                    if (!updated[key]) {
                        // Use stored days and pricePerDay if available, otherwise use defaults
                        const serviceWithDays =
                            service.type === 'special_food' ||
                            service.type === 'medication' ||
                            service.type === 'special_care'
                                ? (service as { days?: number; pricePerDay?: number })
                                : null
                        updated[key] = {
                            days: serviceWithDays?.days ?? (nights || 1),
                            pricePerDay: serviceWithDays?.pricePerDay ?? (service.price || 0),
                        }
                        hasChanges = true
                    }
                }
            })

            return hasChanges ? updated : prev
        })
    }, [servicesKey, nights])
    const hasHighSeason = hasHighSeasonDays(
        { from: new Date(reservation.checkInDate), to: new Date(reservation.checkOutDate) },
        highSeasonPeriods,
    )

    if (periodsLoading || !periods.length) {
        return (
            <Card className='md:col-span-2'>
                <CardHeader>
                    <CardTitle className='text-bold font-medium'>
                        {t('reservation.priceBreakdown.title', 'Desglose de Precio')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className='text-sm text-muted-foreground'>
                        {t('reservation.priceBreakdown.loading', 'Cargando tarifas...')}
                    </p>
                </CardContent>
            </Card>
        )
    }

    const handleAddExtra = () => {
        if (reservation.type !== 'hotel' || !onExtrasChange) return
        if (!newExtra.concept || newExtra.amount === 0) return

        const newExtras = [
            ...(reservation.extras || []),
            {
                id: crypto.randomUUID(),
                concept: newExtra.concept,
                amount: newExtra.amount,
            },
        ]

        onExtrasChange(newExtras)
        setNewExtra({ concept: '', amount: 0 })
    }

    const handleRemoveExtra = (extraId: string) => {
        if (reservation.type !== 'hotel' || !onExtrasChange || !reservation.extras) return

        const newExtras = reservation.extras.filter(e => e.id !== extraId)
        onExtrasChange(newExtras)
    }

    const handleRemoveShopProduct = (productIndex: number) => {
        if (reservation.type !== 'hotel' || !reservation.shopProducts) return
        const newProducts = reservation.shopProducts.filter((_, index) => index !== productIndex)
        onShopProductsChange(newProducts)
    }

    const handleServicePriceChange = (
        petIndex: number | undefined,
        serviceType: AdditionalServiceType,
        newPrice: number,
        days?: number,
        pricePerDay?: number,
    ) => {
        const updatedServices = reservation.additionalServices.map(service => {
            if (service.type === 'driver' && serviceType === 'driver') {
                return { ...service, price: newPrice }
            } else if (service.type === serviceType && 'petIndex' in service && service.petIndex === petIndex) {
                // For daily services, also save days and pricePerDay
                if (
                    (service.type === 'special_food' ||
                        service.type === 'medication' ||
                        service.type === 'special_care') &&
                    days !== undefined &&
                    pricePerDay !== undefined
                ) {
                    return { ...service, price: newPrice, days, pricePerDay }
                }
                return { ...service, price: newPrice }
            }
            return service
        })
        console.log('updatedServices', updatedServices)
        onServicesChange(updatedServices)
    }

    const handleServiceDaysChange = (petIndex: number, serviceType: AdditionalServiceType, newDays: number | '') => {
        const key = `${petIndex}-${serviceType}`
        setServiceInputs(prev => {
            const currentInputs = { ...prev }
            if (!currentInputs[key]) {
                // Get current service to initialize pricePerDay
                const service = reservation.additionalServices.find(
                    s => s.type === serviceType && 'petIndex' in s && s.petIndex === petIndex,
                )
                const serviceWithDays =
                    service &&
                    (service.type === 'special_food' ||
                        service.type === 'medication' ||
                        service.type === 'special_care')
                        ? (service as { days?: number; pricePerDay?: number })
                        : null
                currentInputs[key] = {
                    days: serviceWithDays?.days || nights || 1,
                    pricePerDay: serviceWithDays?.pricePerDay || service?.price || 0,
                }
            }
            currentInputs[key].days = newDays === '' ? '' : newDays

            // Only calculate and update stored price if both values are present
            const days = currentInputs[key].days
            const pricePerDay = currentInputs[key].pricePerDay
            if (days !== '' && pricePerDay !== '' && typeof days === 'number' && typeof pricePerDay === 'number') {
                const storedPrice = nights > 0 ? (days * pricePerDay) / nights : pricePerDay
                handleServicePriceChange(petIndex, serviceType, storedPrice, days, pricePerDay)
            }

            return currentInputs
        })
    }

    const handleServicePricePerDayChange = (
        petIndex: number,
        serviceType: AdditionalServiceType,
        newPricePerDay: number | '',
    ) => {
        const key = `${petIndex}-${serviceType}`
        setServiceInputs(prev => {
            const currentInputs = { ...prev }
            if (!currentInputs[key]) {
                // Get current service to initialize days
                const service = reservation.additionalServices.find(
                    s => s.type === serviceType && 'petIndex' in s && s.petIndex === petIndex,
                )
                const serviceWithDays =
                    service &&
                    (service.type === 'special_food' ||
                        service.type === 'medication' ||
                        service.type === 'special_care')
                        ? (service as { days?: number; pricePerDay?: number })
                        : null
                currentInputs[key] = {
                    days: serviceWithDays?.days || nights || 1,
                    pricePerDay: serviceWithDays?.pricePerDay || service?.price || 0,
                }
            }
            currentInputs[key].pricePerDay = newPricePerDay === '' ? '' : newPricePerDay

            // Only calculate and update stored price if both values are present
            const days = currentInputs[key].days
            const pricePerDay = currentInputs[key].pricePerDay
            if (days !== '' && pricePerDay !== '' && typeof days === 'number' && typeof pricePerDay === 'number') {
                const storedPrice = nights > 0 ? (days * pricePerDay) / nights : pricePerDay
                handleServicePriceChange(petIndex, serviceType, storedPrice, days, pricePerDay)
            }

            return currentInputs
        })
    }

    const handleDriverPriceChange = (priceType: 'pickupPrice' | 'dropoffPrice', newPrice: number) => {
        const updatedServices = reservation.additionalServices.map(service => {
            if (service.type === 'driver') {
                const updatedService = { ...service }
                if (priceType === 'pickupPrice') {
                    updatedService.pickupPrice = newPrice
                } else if (priceType === 'dropoffPrice') {
                    updatedService.dropoffPrice = newPrice
                }
                // Calculate total price as sum of pickup and dropoff
                const totalPrice = (updatedService.pickupPrice || 0) + (updatedService.dropoffPrice || 0)
                updatedService.price = totalPrice
                return updatedService
            }
            return service
        })
        onServicesChange(updatedServices)
    }
    // Group services by pet
    const driverService = reservation.additionalServices.find(s => s.type === 'driver')

    return (
        <Card className='md:col-span-2'>
            <CardHeader>
                <CardTitle className='text-bold font-medium'>
                    {t('reservation.priceBreakdown.title', 'Desglose de Precio')}
                </CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
                {/* Global Stay Price */}
                {reservation.type === 'hotel' && (
                    <div className='space-y-2'>
                        <div className='flex items-center justify-between text-sm text-muted-foreground'>
                            <div className='flex items-center gap-2'>
                                <Bed className='h-4 w-4' />
                                <span>
                                    {nights === 0
                                        ? t('reservation.priceBreakdown.stayPrice.daycare', 'Guardería')
                                        : t(
                                              'reservation.priceBreakdown.stayPrice.stay',
                                              'Estancia ({{nights}} noches)',
                                              {
                                                  nights,
                                              },
                                          )}
                                </span>
                            </div>
                            <div className='flex items-center gap-2'>
                                <span className='text-xs text-muted-foreground'>
                                    {formatCurrency(petsPriceByDay)}/día
                                </span>
                                <span>{formatCurrency(petsPriceByDay * (nights === 0 ? 1 : nights))}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Driver Service */}
                {driverService && (
                    <div>
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center justify-between'>
                                <ServiceItem service={driverService} />
                            </div>

                            {/* Pickup and Dropoff Price Inputs */}
                            <div className='space-y-2 pl-4'>
                                {(driverService.serviceType === 'pickup' || driverService.serviceType === 'both') && (
                                    <div className='flex items-center justify-between'>
                                        <span className='text-sm text-muted-foreground'>
                                            {t('reservation.priceBreakdown.driver.pickup', 'Recogida')}
                                        </span>
                                        <div className='flex items-center gap-2'>
                                            <BufferedNumberInput
                                                key='driver-pickup'
                                                inputSize='sm'
                                                className='w-24'
                                                initialValue={driverService.pickupPrice ?? ''}
                                                placeholder='0'
                                                onDebouncedChange={value =>
                                                    handleDriverPriceChange('pickupPrice', value)
                                                }
                                            />
                                            <span className='text-sm text-muted-foreground'>€</span>
                                        </div>
                                    </div>
                                )}

                                {(driverService.serviceType === 'dropoff' || driverService.serviceType === 'both') && (
                                    <div className='flex items-center justify-between'>
                                        <span className='text-sm text-muted-foreground'>
                                            {t('reservation.priceBreakdown.driver.dropoff', 'Entrega')}
                                        </span>
                                        <div className='flex items-center gap-2'>
                                            <BufferedNumberInput
                                                key='driver-dropoff'
                                                inputSize='sm'
                                                className='w-24'
                                                initialValue={driverService.dropoffPrice ?? ''}
                                                placeholder='0'
                                                onDebouncedChange={value =>
                                                    handleDriverPriceChange('dropoffPrice', value)
                                                }
                                            />
                                            <span className='text-sm text-muted-foreground'>€</span>
                                        </div>
                                    </div>
                                )}

                                {/* Total Driver Service Price */}
                                {driverService.serviceType === 'both' && (
                                    <div className='flex items-center justify-between border-t pt-2'>
                                        <span className='text-sm font-medium'>
                                            {t('reservation.priceBreakdown.driver.total', 'Total transporte')}
                                        </span>
                                        <span className='text-sm font-medium'>
                                            {formatCurrency(driverService.price || 0)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Pets and their services */}
                {reservation.type === 'hotel' &&
                    reservation.pets.map((pet, petIndex) => {
                        const petServices = reservation.additionalServices.filter(
                            s => s.type !== 'driver' && s.petIndex === petIndex,
                        )

                        return (
                            <div key={petIndex}>
                                <h3 className='mb-2 font-medium'>
                                    {pet.name && pet.breed ? `${pet.name} (${pet.breed})` : ''}
                                </h3>
                                <div className='space-y-2 pl-4'>
                                    {petServices.map((service, serviceIndex) => {
                                        const isDailyService =
                                            service.type !== 'hairdressing' && service.type !== 'driver'
                                        const serviceKey = `${petIndex}-${service.type}`
                                        // Use stored values if available, otherwise use defaults
                                        const serviceWithDays =
                                            service.type === 'special_food' ||
                                            service.type === 'medication' ||
                                            service.type === 'special_care'
                                                ? (service as { days?: number; pricePerDay?: number })
                                                : null
                                        const serviceInput = serviceInputs[serviceKey] || {
                                            days: serviceWithDays?.days || nights || 1,
                                            pricePerDay: serviceWithDays?.pricePerDay || service.price || 0,
                                        }

                                        // For hairdressing/driver, show single price input
                                        if (!isDailyService) {
                                            return (
                                                <div
                                                    key={serviceIndex}
                                                    className='flex items-center justify-between gap-2'
                                                >
                                                    <ServiceItem service={service} />
                                                    <div className='flex items-center gap-2'>
                                                        <BufferedNumberInput
                                                            key={serviceKey}
                                                            inputSize='sm'
                                                            className='w-24'
                                                            initialValue={service.price ?? ''}
                                                            placeholder='0'
                                                            onDebouncedChange={newValue =>
                                                                handleServicePriceChange(
                                                                    petIndex,
                                                                    service.type,
                                                                    newValue,
                                                                )
                                                            }
                                                        />
                                                        <span className='text-sm text-muted-foreground'>€</span>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        // For daily services, show two inputs: days and price per day
                                        const totalPriceForService =
                                            typeof serviceInput.days === 'number' &&
                                            typeof serviceInput.pricePerDay === 'number'
                                                ? serviceInput.days * serviceInput.pricePerDay
                                                : 0

                                        return (
                                            <div key={serviceIndex} className='flex items-center justify-between gap-2'>
                                                <ServiceItem service={service} />
                                                <div className='flex items-center gap-2'>
                                                    {/* Number of days input */}
                                                    <div className='flex items-center gap-1'>
                                                        <Input
                                                            inputSize='sm'
                                                            type='number'
                                                            className='w-16'
                                                            value={serviceInput.days === '' ? '' : serviceInput.days}
                                                            placeholder={nights?.toString() || '0'}
                                                            onChange={e => {
                                                                const value = e.target.value
                                                                const newDays =
                                                                    value === ''
                                                                        ? ''
                                                                        : value
                                                                          ? parseInt(value, 10)
                                                                          : nights || 1
                                                                handleServiceDaysChange(petIndex, service.type, newDays)
                                                            }}
                                                        />
                                                        <span className='text-xs text-muted-foreground'>días</span>
                                                    </div>
                                                    {/* Price per day input */}
                                                    <div className='flex items-center gap-1'>
                                                        <Input
                                                            inputSize='sm'
                                                            type='number'
                                                            className='w-20'
                                                            value={
                                                                serviceInput.pricePerDay === ''
                                                                    ? ''
                                                                    : serviceInput.pricePerDay
                                                            }
                                                            placeholder={'0'}
                                                            onChange={e => {
                                                                const value = e.target.value
                                                                const newPricePerDay =
                                                                    value === '' ? '' : value ? parseFloat(value) : 0
                                                                handleServicePricePerDayChange(
                                                                    petIndex,
                                                                    service.type,
                                                                    newPricePerDay,
                                                                )
                                                            }}
                                                        />
                                                        <span className='text-xs text-muted-foreground'>€/día</span>
                                                    </div>
                                                    {/* Total price display */}
                                                    {typeof serviceInput.days === 'number' &&
                                                        typeof serviceInput.pricePerDay === 'number' && (
                                                            <span className='text-xs text-muted-foreground'>
                                                                = {formatCurrency(totalPriceForService)}
                                                            </span>
                                                        )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    })}

                {/* Shop Products */}
                {reservation.type === 'hotel' && (
                    <div>
                        <div className='mb-2 flex items-center justify-between'>
                            <h3 className='font-medium'>
                                {t('reservation.priceBreakdown.shopProducts.title', 'Productos de tienda')}
                            </h3>
                            <Button
                                size='sm'
                                variant='outline'
                                className='flex items-center gap-1'
                                onClick={onGoToShop}
                            >
                                <ShoppingBag className='h-4 w-4' />
                                <span>{t('reservation.priceBreakdown.shopProducts.goToShop', 'Ir a la tienda')}</span>
                            </Button>
                        </div>

                        {reservation.shopProducts && reservation.shopProducts.length > 0 ? (
                            <div className='space-y-2 pl-4'>
                                {reservation.shopProducts.map((product, index) => (
                                    <div
                                        key={index}
                                        className='flex items-center justify-between text-sm text-muted-foreground'
                                    >
                                        <div className='flex items-center gap-2'>
                                            <ShoppingBag className='h-4 w-4' />
                                            <span>
                                                {product.name} (x{product.quantity})
                                            </span>
                                        </div>
                                        <div className='flex items-center gap-2'>
                                            <span className='text-xs text-muted-foreground'>
                                                {formatCurrency(product.unitPrice)}/ud
                                            </span>
                                            <span>{formatCurrency(product.totalPrice)}</span>
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() => handleRemoveShopProduct(index)}
                                                className='h-6 w-6'
                                                aria-label={t(
                                                    'reservation.priceBreakdown.shopProducts.remove',
                                                    'Eliminar producto de tienda',
                                                )}
                                            >
                                                <Trash2 className='h-4 w-4 text-destructive' />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='pl-4 text-sm text-muted-foreground'>
                                {t('reservation.priceBreakdown.shopProducts.noProducts', 'No hay productos de tienda')}
                            </div>
                        )}
                    </div>
                )}

                {/* Extras */}
                {reservation.type === 'hotel' && (
                    <div>
                        <div className='mb-2 flex items-center justify-between'>
                            <h3 className='font-medium'>
                                {t('reservation.priceBreakdown.extras.title', 'Extras y descuentos')}
                            </h3>
                        </div>

                        {/* Lista de extras existentes */}
                        {reservation.extras && reservation.extras.length > 0 ? (
                            <div className='space-y-2 pl-4'>
                                {reservation.extras.map(extra => (
                                    <div
                                        key={extra.id}
                                        className='flex items-center justify-between text-sm text-muted-foreground'
                                    >
                                        <span>{extra.concept}</span>
                                        <div className='flex items-center gap-2'>
                                            <span>{formatCurrency(extra.amount)}</span>
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() => handleRemoveExtra(extra.id)}
                                                className='h-6 w-6'
                                            >
                                                <Trash2 className='h-4 w-4 text-destructive' />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='pl-4 text-sm text-muted-foreground'>
                                {t('reservation.priceBreakdown.extras.empty', 'No hay extras o descuentos')}
                            </div>
                        )}

                        {/* Formulario para añadir nuevo extra */}
                        <div className='mt-4 flex items-end gap-2'>
                            <div className='flex-1'>
                                <Input
                                    placeholder={t('reservation.priceBreakdown.extras.conceptPlaceholder', 'Concepto')}
                                    value={newExtra.concept}
                                    onChange={e => setNewExtra({ ...newExtra, concept: e.target.value })}
                                />
                            </div>
                            <div className='w-24'>
                                <Input
                                    type='number'
                                    placeholder={'0'}
                                    value={newExtra.amount || ''}
                                    onChange={e =>
                                        setNewExtra({
                                            ...newExtra,
                                            amount: e.target.value ? Number(e.target.value) : 0,
                                        })
                                    }
                                />
                            </div>
                            <Button variant='outline' size='lg' onClick={handleAddExtra}>
                                <Plus className='' />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Advance Payment (50%) - Only shown when there are high season days */}
                {hasHighSeason && (
                    <div className='mt-6 rounded-lg border-2 border-yellow-500 bg-yellow-50 p-4 dark:bg-yellow-950/20'>
                        <div className='flex items-center justify-between'>
                            <span className='text-base font-bold text-yellow-900 dark:text-yellow-200'>
                                {t('reservation.priceBreakdown.advancePayment', '50% por adelantado en temporada alta')}
                            </span>
                            <span className='text-lg font-bold text-yellow-900 dark:text-yellow-200'>
                                {formatCurrency(total * 0.5)}
                            </span>
                        </div>
                        <p className='mt-2 text-xs text-yellow-800 dark:text-yellow-300'>
                            {t(
                                'reservation.priceBreakdown.advancePaymentNote',
                                'Una vez efectuado el pago, no se admiten cancelaciones ni reembolsos, sin excepción, independientemente del motivo.',
                            )}
                        </p>
                    </div>
                )}

                {/* Total */}
                <div className='mt-6 flex items-center justify-between border-t pt-4'>
                    <h3 className='text-lg font-semibold'>{t('reservation.priceBreakdown.total', 'Total')}</h3>
                    <span className='text-lg font-semibold'>{formatCurrency(total)}</span>
                </div>
            </CardContent>
        </Card>
    )
}
